import { getOwner, Owner, Show, Suspense, runWithOwner } from 'solid-js';

import { RootContext, SusRootContext, type SusRootState } from './globalStore';
import { mountEnv } from './env';
import { mountSetting } from './setting';

import type { PromiseResolve } from '~/constants';
import { useEvtBus, Events } from '~/hook/useEvtBus';

/** Prepare commonly-used global stores at app init to simplify shared data throughout the lifecycle */
export function InitDepends(
  props: ParentProps<{
    /** Async global stores */
    sources: Accessor<any>[];
  }>
) {
  function AsyncStores() {
    const sources = props.sources.map((it) => it());
    function isInited() {
      return import.meta.env.SSR ? sources.every((it) => it !== undefined) : true;
    }
    return <Show when={isInited()}>{props.children}</Show>;
  }

  return (
    <RootProvider>
      <Suspense>
        <AsyncStores />
      </Suspense>
    </RootProvider>
  );
}

function RootProvider(props: ParentProps) {
  const runners = new Map<() => any, any>();
  let owner!: Owner | null;
  mountEnv();
  mountSetting();
  const runner = (fn: () => any) => {
    let res = runners.get(fn);
    if (!res) {
      res = runWithOwner(owner, fn);
      runners.set(fn, res);
    }
    return res;
  };
  const preloadRoute = usePreloadRoute();
  const location = useLocation();

  // Preload after children to avoid env/setting context read errors
  untrack(() => {
    preloadRoute(location.pathname + location.search, {
      preloadData: true,
    });
  });
  return <RootContext.Provider value={runner}>{((owner = getOwner()), props.children)}</RootContext.Provider>;
}

export function SusRootProvider(props: ParentProps) {
  const state = createStore<SusRootState>({
    count: 0,
    pending: () => void 0,
  });

  const resolveImmediate = () => {
    state[1]('count', 0);
    state[0].pending(false);
  };

  let timer: NodeJS.Timeout | undefined;
  const resolvePending = (force: boolean = false) => {
    if (force) {
      clearTimeout(timer);
      resolveImmediate();
      return;
    }

    const { pendingDelay = 620, pendingTimer = 0 } = state[0];

    const nowTime = new Date().getTime();
    const diffTime = nowTime - pendingTimer;

    if (diffTime >= pendingDelay) {
      resolveImmediate();
    } else {
      timer = setTimeout(() => {
        resolveImmediate();
      }, pendingDelay - diffTime);
    }
  };

  createEffect<PromiseResolve<boolean>>((prev) => {
    prev && prev(false);

    /** Force-end loading after 30 s */
    let timer = setTimeout(() => {
      resolvePending(true);
    }, 20000);

    onCleanup(() => {
      clearTimeout(timer);
    });

    return state[0].pending;
  });

  useBeforeLeave(() => {
    resolvePending(true);
  });

  useEvtBus(Events.PENDING_REGISTER, () => {
    // console.log('===walker===PENDING_REGISTER', untrack(() => state[0].count + 1));
    state[1]('count', (p) => p + 1);
  });

  useEvtBus(Events.PENDING_RESOLVE, () => {
    const count = untrack(() => state[0].count - 1);
    // console.log('===walker===PENDING_RESOLVE', count);
    state[1]('count', (p) => p - 1);

    count <= 0 && resolvePending();
  });

  useEvtBus(Events.PENDING_DONE, () => {
    resolvePending(true);
  });

  return <SusRootContext.Provider value={state}>{props.children}</SusRootContext.Provider>;
}
